<template>
    <b-modal :id="modalId" size="lg" centered title-tag="div" :header-class="headerClass"
        :content-class="contentClass" @show="onShow" @hidden="onHidden">
        <template #modal-title>
            <brand-modal-header :sandbox-mode="sandboxMode" :title="title" :animate="animateIn" />
        </template>

        <div class="welcome-content">
            <p v-for="(paragraph, index) in introParagraphs" :key="index" class="mb-3"
                :class="riseClass" :style="riseDelay(240 + index * 70)">
                {{ paragraph }}
            </p>

            <b-alert v-if="sandboxMode" variant="info" show class="mb-0 welcome-note"
                :class="riseClass" :style="riseDelay(380)">
                <strong>One thing to know:</strong> this is not a fully connected environment.
                Texting, integration partners, printing and scanning are all switched off, and the
                walkthrough uses manual entry to stand in for those steps.
            </b-alert>

            <!-- Nobody by name and no address on purpose: every trial is run by whoever set it up,
                 which is why the sandbox guide says "your primary Scriptly contact" as well. -->
            <p v-if="sandboxMode" class="text-muted mt-3 mb-0" :class="riseClass"
                :style="riseDelay(450)">
                <small>Questions about the trial? Get in touch with your primary Scriptly contact.</small>
            </p>
        </div>

        <template #modal-footer>
            <div class="d-flex flex-wrap justify-content-between align-items-center w-100"
                :class="riseClass" :style="riseDelay(520)">
                <b-form-checkbox v-if="showDismissCheckbox" v-model="dontShowAgain">
                    Don't show this again
                </b-form-checkbox>
                <span v-else />

                <div class="d-flex align-items-center">
                    <span v-if="showTourButton && tourBlockedNote" class="text-muted mr-3">
                        <small>{{ tourBlockedNote }}</small>
                    </span>
                    <b-button variant="link" class="brand-modal-link"
                        @click="openDocumentation">
                        Browse the documentation
                    </b-button>
                    <b-button v-if="showTourButton" variant="primary" class="ml-2 brand-modal-action"
                        :disabled="!!tourBlockedNote" @click="takeTour">
                        {{ tourButtonLabel }}
                    </b-button>
                </div>
            </div>
        </template>
    </b-modal>
</template>

<script lang="ts">
import { Component, Prop, Vue } from "vue-property-decorator";
import BrandModalHeader from "@/components/Modals/BrandModalHeader.vue";
import {
    BRAND_MODAL_RISE,
    brandModalContentClass,
    brandModalHeaderClass,
    brandModalRiseDelay,
} from "@/components/Modals/brandModalHeader";
import { dismissWelcome } from "@/services/welcomeService";
import { pushIgnoringDuplicate } from "@/util/routerNavigation";
import { TOUR_MIN_WIDTH, tourBlockedNote, tourBlockedReason } from "@/tour/types";

/** What the user asked for on the way out, acted on once the modal has finished hiding. */
type PendingAction = "tour" | "documentation" | null;

/**
 * The first-login greeting, hosted once in the footer so the footer link and the layout's auto-show
 * gate share one instance. Follows the pattern HotkeyDiscoveryModal already uses: a plain b-modal
 * with a public `show()`.
 *
 * Both the tour and the documentation link act on the modal's `hidden` event rather than
 * immediately, because starting a tour under a modal backdrop puts the driver.js overlay and the
 * backdrop on top of each other.
 */
@Component({ components: { BrandModalHeader } })
export default class WelcomeModal extends Vue {
    @Prop({ type: String, default: "welcome-modal" }) modalId!: string;

    // `type: Boolean` on both: without emitDecoratorMetadata the TypeScript type never reaches
    // Vue, and an untyped prop is not cast, so a valueless attribute would arrive as "".
    /** Trial tenant: different copy, a relabelled tour button, and no dismiss checkbox. */
    @Prop({ type: Boolean, default: false }) sandboxMode!: boolean;

    /** tenantConfig.disableGuidedTour: hides the tour button entirely. */
    @Prop({ type: Boolean, default: false }) tourDisabled!: boolean;

    protected dontShowAgain = false;
    private pendingAction: PendingAction = null;

    /** True from `shown` until `hidden`; see `riseClass` for why it is not just a CSS animation. */
    protected animateIn = false;

    /** window.innerWidth is not reactive, so track it for the duration the modal is mounted. */
    protected viewportWidth = typeof window === "undefined" ? TOUR_MIN_WIDTH : window.innerWidth;

    created() {
        window.addEventListener("resize", this.onResize);
    }

    beforeDestroy() {
        window.removeEventListener("resize", this.onResize);
    }

    protected onResize() {
        this.viewportWidth = window.innerWidth;
    }

    get firstName(): string {
        return this.$user?.firstName?.trim() ?? "";
    }

    /**
     * The body's share of the entrance stagger; the band runs its own from the same class.
     *
     * Set from `show`, not `shown`. `shown` fires only once the backdrop fade has finished, by
     * which point the contents have been on screen at full opacity for a few hundred milliseconds
     * -- so applying the class then blinked them out and faded them back in, a flash on every
     * open. `show` fires before the fade begins, and several ticks before the modal paints.
     */
    get riseClass(): string {
        return this.animateIn ? BRAND_MODAL_RISE : "";
    }

    /** Per-element delay for that stagger. Inert while `riseClass` is empty. */
    protected riseDelay(ms: number): Record<string, string> {
        return brandModalRiseDelay(ms);
    }

    /** What Bootstrap Vue puts on `.modal-header` so the shared band paints. */
    get headerClass(): (string | Record<string, boolean>)[] {
        return brandModalHeaderClass(this.sandboxMode);
    }

    /** And on `.modal-content`, so the band's tone reaches both actions below it. */
    get contentClass(): (string | Record<string, boolean>)[] {
        return brandModalContentClass(this.sandboxMode);
    }

    /** Trial logins are never given a first or last name, so that title never carries one. */
    get title(): string {
        if (this.sandboxMode) return "Welcome to the Scriptly Trial Experience";
        return this.firstName ? `Welcome to Scriptly, ${this.firstName}` : "Welcome to Scriptly";
    }

    get introParagraphs(): string[] {
        if (this.sandboxMode) {
            return [
                "This is a trial environment set up for you to explore Scriptly hands-on with demo data. Work through it however you like — nothing you do here reaches a real pharmacy.",
                "The sandbox walkthrough follows the trial guide section by section, from program configuration through processing a prescription end to end.",
            ];
        }
        return [
            "Scriptly is where your pharmacy's prescriptions, patients, orders and billing all live. The sidebar groups every area by workflow, and the search box at the top finds a patient or a prescription from anywhere in the application.",
            "Take the guided tour for a short walkthrough of the pages you will use most, or browse the documentation whenever you want the detail.",
        ];
    }

    get showTourButton(): boolean {
        return !this.tourDisabled;
    }

    get tourButtonLabel(): string {
        return this.sandboxMode ? "Start the sandbox walkthrough" : "Take the guided tour";
    }

    /**
     * The checkbox is the only thing that writes the dismiss preference, so it is not rendered on a
     * sandbox tenant: the login there is shared, and one trial user ticking it would silence the
     * modal for everyone. The gate ignores the stored preference on sandbox for the same reason.
     */
    get showDismissCheckbox(): boolean {
        return !this.sandboxMode;
    }

    /** Non-empty when a run cannot start: the button is disabled and this explains why. */
    get tourBlockedNote(): string {
        return tourBlockedNote(tourBlockedReason(this.viewportWidth));
    }

    protected takeTour() {
        this.pendingAction = "tour";
        this.hide();
    }

    protected openDocumentation() {
        this.pendingAction = "documentation";
        this.hide();
    }

    protected onShow() {
        this.animateIn = true;
    }

    protected onHidden() {
        this.animateIn = false;

        // The checkbox decides this, whichever control closed the modal. Starting a tour is not an
        // implicit dismissal, so with the box unticked nothing is written.
        if (this.dontShowAgain) dismissWelcome();

        const action = this.pendingAction;
        this.pendingAction = null;

        if (action === "tour") this.$emit("start-tour");
        // Tolerates the user already being there: the footer link opens this modal on any page.
        if (action === "documentation") void pushIgnoringDuplicate(this.$router, "/documentation/welcome");
    }

    public show() {
        this.$bvModal.show(this.modalId);
    }

    public hide() {
        this.$bvModal.hide(this.modalId);
    }
}
</script>
<style scoped>
/* The band and every action's tone live in `_brand-modal.scss`, shared with the guided tour hub.
   What is left here is this modal's own. */

.alert.welcome-note {
    /* Drawn from the trial band's own teal rather than Bootstrap's stock info blue, and flat --
       a gradient behind a paragraph of text fights it. Text on this tint reads at 8:1.
       `.alert` sets `border: 0`, so the width has to come back alongside the colour, and naming
       `.alert` here clears the theme's `.alert.alert-info`, which ties with a class alone. */
    border: 1px solid #b6d9dc;
    background-color: #e4f1f2;
    color: #0a4f57;
}
</style>
